import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Video, MessageCircle, X, Reply, Calendar, UserPlus } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { getCurrentUser } from '../../utils/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  playClassNotificationSound,
  playChatNotificationSound,
  playEnquiryNotificationSound,
} from '../../utils/notificationSound';
import { routeNotification } from '../../utils/notificationManager';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-26 (Phase 5); NotificationToast.scss deleted.
//
// 🔴 THE PALETTE DECISION, made explicitly (owner, 2026-08-26): the toast keeps a
// colour IDENTITY, and that identity becomes the institute's.
//
// The panel was a fixed Microsoft-Teams violet (`#464775 → #4f4f8a`) with ~40
// `rgba(255,255,255,x)` inks over it. Two ways to convert it were wrong:
//
//   · flatten it onto `--surface-dark` — a slate panel. Correct by the rules and
//     worse in fact: the toast stops being recognisable at a glance, which is the
//     one job an 8-second interruption has.
//   · leave the hex — an institute that picks its colour still gets Teams violet
//     shouting at it three times an hour.
//
// So the panel is `color-mix()` from `--surface-dark` towards the brand, which is
// the idiom UpgradeBanner and TenantAdminLogin already use for an always-dark
// surface that must still look like ours. It stays dark in both themes on purpose
// — that is what `--surface-dark` is for — and the inks are `on-surface-dark` at
// the alphas the sheet was already writing by hand.
//
// ⚠️ Its `:root[data-theme="light"]` block was DEAD: it restated the base gradient
// byte for byte. The dark block was real but only nudged four alphas by 5 points
// and darkened the panel; an always-dark surface does not need either, and
// `--popover-shadow` already deepens on its own.
//
// The `new_enquiry` variant was green (`#1b4332 → #2d6a4f`) and stays green — a
// new lead is good news, and that is `--success`, mixed the same way.
//
// Two keyframes went to the config: `dialog-in` was already there and is reused
// (30px → 12px rise, which is the same motion at the vocabulary's distance), and
// `dialog-out` is new, because this is the first surface in the app that animates
// its own dismissal. The progress bar reuses `countdown` — the keyframe declared
// with NO duration precisely so a component can set `animationDuration` inline,
// which is what this one does from its own timeout.

const CONTAINER =
  'tw-pointer-events-none tw-fixed tw-bottom-5 tw-right-5 tw-z-toast tw-flex tw-w-full ' +
  'tw-max-w-[340px] tw-flex-col-reverse tw-gap-2.5 ' +
  'max-[480px]:tw-bottom-2 max-[480px]:tw-left-2 max-[480px]:tw-right-2 ' +
  'max-[480px]:tw-max-w-[calc(100vw-16px)]';

const TOAST =
  'tw-pointer-events-auto tw-relative tw-overflow-hidden tw-rounded-[6px] ' +
  'tw-shadow-[0_4px_24px_var(--popover-shadow),0_1px_6px_var(--card-shadow)]';

// Stop one holds more of the dark surface, stop two more of the hue — the same
// direction the two hand-picked stops ran in.
const PANEL_BRAND =
  'tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--surface-dark))_66%,hsl(var(--primary))),color-mix(in_srgb,hsl(var(--surface-dark))_56%,hsl(var(--primary))))]';

const PANEL_SUCCESS =
  'tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--surface-dark))_70%,hsl(var(--success))),color-mix(in_srgb,hsl(var(--surface-dark))_55%,hsl(var(--success))))]';

const PANEL = {
  class_started: PANEL_BRAND,
  chat_message: PANEL_BRAND,
  new_enquiry: PANEL_SUCCESS,
};

const CLOSE_BTN =
  'tw-absolute tw-right-1.5 tw-top-1.5 tw-z-[2] tw-flex tw-h-6 tw-w-6 tw-cursor-pointer ' +
  'tw-items-center tw-justify-center tw-rounded-[4px] tw-border-none tw-bg-transparent ' +
  'tw-text-on-surface-dark/60 tw-opacity-0 tw-transition-all tw-duration-150 ' +
  'group-hover:tw-opacity-100 hover:tw-bg-on-surface-dark/15 hover:tw-text-on-surface-dark';

const PROGRESS =
  'tw-absolute tw-bottom-0 tw-left-0 tw-h-0.5 tw-w-full tw-origin-left ' +
  'tw-animate-countdown tw-bg-on-surface-dark/35';

const MEETING_ICON =
  'tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded ' +
  'tw-border-2 tw-border-solid tw-border-on-surface-dark/25 tw-bg-on-surface-dark/15 ' +
  'tw-text-on-surface-dark';

const MEETING_TITLE =
  'tw-mb-1 tw-max-w-full tw-truncate tw-text-sm tw-font-medium tw-text-on-surface-dark';

const ACTION_BTN =
  'tw-flex tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 ' +
  'tw-border-none tw-bg-transparent tw-px-0 tw-py-2.5 tw-text-[13px] tw-font-semibold ' +
  'tw-text-on-surface-dark/85 tw-transition-all tw-duration-150 ' +
  'hover:tw-bg-on-surface-dark/10 hover:tw-text-on-surface-dark';

const ACTION_BAR =
  'tw-flex tw-border-0 tw-border-t tw-border-solid tw-border-on-surface-dark/[0.12]';

const CHAT_BODY = 'tw-flex tw-items-center tw-gap-3 tw-px-4 tw-pb-2.5 tw-pt-3.5';

const AVATAR_BASE =
  'tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full ' +
  'tw-text-on-surface-dark';

// A pale tint of the same hue, so the avatar reads as part of the panel rather
// than as a sticker on it.
const AVATAR_CHAT =
  `${AVATAR_BASE} tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--primary))_50%,white),color-mix(in_srgb,hsl(var(--primary))_38%,white))]`;

const AVATAR_ENQUIRY =
  `${AVATAR_BASE} tw-bg-[linear-gradient(135deg,color-mix(in_srgb,hsl(var(--success))_75%,white),color-mix(in_srgb,hsl(var(--success))_60%,white))]`;

const CHAT_SENDER =
  'tw-mb-0.5 tw-truncate tw-text-[13.5px] tw-font-medium tw-text-on-surface-dark';

const CHAT_MESSAGE =
  'tw-line-clamp-2 tw-text-[12.5px] tw-leading-[1.4] tw-text-on-surface-dark/[0.72]';

const CHAT_ICON_BTN =
  'tw-flex tw-h-[34px] tw-w-[34px] tw-shrink-0 tw-cursor-pointer tw-items-center ' +
  'tw-justify-center tw-rounded-[6px] tw-border-none tw-bg-transparent ' +
  'tw-text-on-surface-dark/55 tw-transition-all tw-duration-150 ' +
  'hover:tw-bg-on-surface-dark/[0.12] hover:tw-text-on-surface-dark';

const REPLY_BTN =
  'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-none ' +
  'tw-bg-transparent tw-px-4 tw-py-[9px] tw-text-[13px] tw-font-semibold ' +
  'tw-text-on-surface-dark/80 tw-transition-all tw-duration-150 ' +
  'hover:tw-bg-on-surface-dark/10 hover:tw-text-on-surface-dark';

const TOAST_DURATION = 8000;
const CHAT_TOAST_DURATION = 6000;
const MAX_TOASTS = 3;

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0][0]?.toUpperCase() || '?';
}

const NotificationToast = () => {
  const { socket } = useSocket();
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const location = useLocation();
  const user = getCurrentUser();
  const navigate = useNavigate();

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback((toast) => {
    const id = ++toastIdRef.current;
    const newToast = { ...toast, id, exiting: false, createdAt: Date.now() };

    setToasts(prev => {
      const updated = [...prev, newToast];
      if (updated.length > MAX_TOASTS) {
        return updated.slice(updated.length - MAX_TOASTS);
      }
      return updated;
    });

    const duration = toast.type === 'class_started' ? TOAST_DURATION : CHAT_TOAST_DURATION;
    setTimeout(() => removeToast(id), duration);

    return id;
  }, [removeToast]);

  // ── Listen for class_started notifications ──
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data) => {
      if (data.type === 'class_started') {
        // No tag of our own: the key is derived from the event (see eventKey),
        // so the push for this same class start is recognised as a duplicate.
        const route = routeNotification({
          type: 'class_started',
          batchId: data.batchId,
        });

        if (!route.show) return;

        // In-app: show toast + play sound
        if (route.method === 'in-app') {
          playClassNotificationSound();
          addToast({
            type: 'class_started',
            title: data.batchName || data.title || 'Class Started',
            message: data.message || 'The meeting has started',
            batchId: data.batchId,
          });
        }
        // Background: SW handles native push; just play sound if tab is still alive
      }
    };

    socket.on('notification', handleNotification);
    return () => socket.off('notification', handleNotification);
  }, [socket, addToast, navigate, user?.usertype]);

  // ── Listen for chat messages when NOT on the chat page ──
  useEffect(() => {
    if (!socket) return;

    const handleChatNotification = (data) => {
      const currentUserId = user?._id || user?.id;
      if (data.sender === currentUserId) return;

      const isOnChatPage = location.pathname.includes('/chat');
      if (isOnChatPage) return;

      const batchId = data.batch?._id || data.batch;
      const route = routeNotification({
        type: 'chat_message',
        batchId,
      });

      if (!route.show) return;

      if (route.method === 'in-app') {
        playChatNotificationSound();

        const msgPreview = data.message
          ? (data.message.length > 60 ? data.message.substring(0, 60) + '...' : data.message)
          : 'Sent an attachment';

        addToast({
          type: 'chat_message',
          title: data.senderName || 'New Message',
          message: msgPreview,
          senderName: data.senderName,
          batchName: data.batchName,
          batchId,
        });
      }
      // Background: SW handles native push
    };

    socket.on('chat:notification', handleChatNotification);
    return () => socket.off('chat:notification', handleChatNotification);
  }, [socket, addToast, navigate, location.pathname, user]);

  // ── Listen for new enquiry notifications (from Zoho / public forms) ──
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data) => {
      if (data.type !== 'new_enquiry') return;

      // Only show to admins
      const ut = user?.usertype;
      if (!['admin', 'superadmin'].includes(ut)) return;

      // Don't show if already on enquiries page
      if (location.pathname.includes('/enquiries')) return;

      playEnquiryNotificationSound();
      addToast({
        type: 'new_enquiry',
        title: data.title || 'New Enquiry',
        message: data.message || data.fullName || 'A new lead has arrived',
        link: data.link,
        enquiryId: data.enquiryId,
        source: data.source,
      });
    };

    socket.on('notification', handleNotification);
    return () => socket.off('notification', handleNotification);
  }, [socket, addToast, location.pathname, user]);

  const handleJoinClass = (toast) => {
    removeToast(toast.id);
    const usertype = user?.usertype;
    if (usertype === 'student') {
      navigate(`/s/classroom/${toast.batchId}`);
    } else if (usertype === 'instructor') {
      navigate(`/i/classroom/${toast.batchId}`);
    }
  };

  const handleGoToChat = (toast) => {
    removeToast(toast.id);
    const prefix = user?.usertype === 'student' ? '/s' : user?.usertype === 'instructor' ? '/i' : '';
    navigate(`${prefix}/chat`);
  };

  const handleGoToEnquiry = (toast) => {
    removeToast(toast.id);
    if (toast.link) {
      navigate(toast.link);
    } else {
      navigate('/enquiries');
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className={CONTAINER}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          // `group` replaces `.teams-toast:hover &` — the close button only
          // appears while the pointer is over the toast it belongs to.
          className={`tw-group ${TOAST} ${PANEL[toast.type] || PANEL_BRAND} ${
            toast.exiting ? 'tw-animate-dialog-out' : 'tw-animate-dialog-in'
          }`}
        >
          {/* Close button */}
          <button
            className={CLOSE_BTN}
            onClick={() => removeToast(toast.id)}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>

          {toast.type === 'class_started' ? (
            /* ── Meeting Started (centered card layout) ── */
            <>
              <div className="tw-h-1.5 tw-bg-[linear-gradient(90deg,hsl(var(--primary)),color-mix(in_srgb,hsl(var(--primary))_78%,black))]" />
              <div className="tw-flex tw-flex-col tw-items-center tw-px-5 tw-pb-3.5 tw-pt-5 tw-text-center">
                <div className={MEETING_ICON}>
                  <Calendar size={22} />
                </div>
                <div className={MEETING_TITLE}>{toast.title}</div>
                <div className="tw-text-xs tw-leading-[1.4] tw-text-on-surface-dark/70">{toast.message}</div>
              </div>
              <div className={ACTION_BAR}>
                <button
                  className={`${ACTION_BTN} tw-border-0 tw-border-r tw-border-solid tw-border-on-surface-dark/[0.12]`}
                  onClick={() => handleJoinClass(toast)}
                >
                  <Video size={16} />
                  <span>Join</span>
                </button>
                <button
                  className={ACTION_BTN}
                  onClick={() => handleGoToChat(toast)}
                >
                  <MessageCircle size={16} />
                  <span>Message</span>
                </button>
              </div>
            </>
          ) : toast.type === 'new_enquiry' ? (
            /* ── New Enquiry / Zoho Lead ── */
            <>
              <div className={CHAT_BODY}>
                <div className={AVATAR_ENQUIRY}>
                  <UserPlus size={18} />
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className={CHAT_SENDER}>{toast.title}</div>
                  <div className={CHAT_MESSAGE}>{toast.message}</div>
                  {toast.source && (
                    <div className={`${CHAT_MESSAGE} tw-text-[0.7rem] tw-opacity-70`}>
                      Source: {toast.source}
                    </div>
                  )}
                </div>
              </div>
              <div className={ACTION_BAR}>
                <button
                  className={REPLY_BTN}
                  onClick={() => handleGoToEnquiry(toast)}
                >
                  <UserPlus size={16} />
                  <span>View Enquiry</span>
                </button>
              </div>
            </>
          ) : (
            /* ── Chat Message (horizontal layout) ── */
            <>
              <div className={CHAT_BODY}>
                <div className={AVATAR_CHAT}>
                  <span className="tw-text-sm tw-font-medium tw-tracking-[0.5px]">{getInitials(toast.senderName)}</span>
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                  <div className={CHAT_SENDER}>{toast.title}</div>
                  <div className={CHAT_MESSAGE}>{toast.message}</div>
                </div>
                <button
                  className={CHAT_ICON_BTN}
                  onClick={() => handleGoToChat(toast)}
                  title="Open chat"
                >
                  <MessageCircle size={18} />
                </button>
              </div>
              <div className={ACTION_BAR}>
                <button
                  className={REPLY_BTN}
                  onClick={() => handleGoToChat(toast)}
                >
                  <Reply size={16} />
                  <span>Reply</span>
                </button>
              </div>
            </>
          )}

          {/* Progress bar — `countdown` carries no duration of its own so this
              inline longhand wins; see tailwind.config.js. */}
          <div
            className={PROGRESS}
            style={{
              animationDuration: `${toast.type === 'class_started' ? TOAST_DURATION : CHAT_TOAST_DURATION}ms`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
